import { ReactElement, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { StatusBar, ImageUpload, Input, PrimaryButton, Typography } from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import styles from './MenuModal.module.scss';
import { useNavigate, useLocation } from 'react-router-dom';
import { IFormData, IMenuModal, TVariant } from './types';
import { IDuplicateMenuRequest, IMenuRequest } from '../../api/types';
import { duplicateMenuOnSubmit } from './duplicateMenuOnSubmit/duplicateMenuOnSubmit';
import { createAndUpdateMenuOnSubmit } from './createAndUpdateMenuOnSubmit/createAndUpdateMenuOnSubmit';
import { QueryKeys } from '../../hooks/QueryKeys';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { useRanges } from 'src/hooks/useRanges/useRanges';
import { ProductRangeSelector } from '../ProductRangeSelector/ProductRangeSelector';
import { FormVariant } from '../../types/menuItemForm.types';

const MenuModalContent = ({
  menuName,
  title,
  closeModal,
  variant,
  id,
  consumerName,
  image,
}: IMenuModal): ReactElement => {
  const queryClient = useQueryClient();
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { trackEvent, captureException } = useUserTracking();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const [uploadedImage, setUploadedImage] = useState<File | string | null>(image);
  const isImageObject = typeof uploadedImage === 'object';
  const { data: ranges } = useRanges({ enabled: variant === 'create' });

  const isDuplicate = variant === 'duplicate';

  const buttonTextLookup: Record<TVariant, string> = {
    create: 'Create',
    update: 'Save',
    duplicate: 'Duplicate',
  };

  const defaultValues: IFormData = {
    name: (isDuplicate ? `${menuName} (Copy)` : menuName) || '',
    consumerName: consumerName || '',
    files: uploadedImage,
    source: 'library',
    ranges: [],
  };

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    register,
    watch,
    setValue,
    setError,
    clearErrors,
    control,
  } = useForm<IFormData>({
    defaultValues,
  });
  const menuSource = watch('source');

  useEffect(() => {
    if (menuSource === 'library') {
      clearErrors('ranges');
    }
  }, [menuSource, clearErrors]);

  const onSubmit = async (data: IFormData) => {
    clearError();
    if (menuSource === 'ranges' && (!data.ranges || data.ranges.length === 0)) {
      setError('ranges', { message: 'Must select one or more ranges before you can create your menu' });
      return;
    }

    const payload: IMenuRequest = {
      name: data.name,
      consumer_name: data.consumerName,
    };

    const payloadCreateOrUpdate =
      variant === FormVariant.UPDATE
        ? payload
        : {
            ...payload,
            ranges: JSON.stringify(data.ranges),
          };

    const payloadDuplicate: IDuplicateMenuRequest = {
      name: data.name,
      consumer_name: data.consumerName,
    };

    if (uploadedImage && isImageObject) {
      payloadCreateOrUpdate.files = uploadedImage;
      payloadDuplicate.files = uploadedImage;
    }
    try {
      isDuplicate
        ? await duplicateMenuOnSubmit(id, payloadDuplicate, closeModal, showNotification, navigate, pathname)
        : await createAndUpdateMenuOnSubmit(variant, closeModal, id, payloadCreateOrUpdate, showNotification);

      const eventMap = { create: 'menu_created', update: 'menu_updated', duplicate: 'menu_duplicated' } as const;
      trackEvent(eventMap[variant], { menuName: data.name, variant });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menus] });
    } catch (e) {
      captureException(e, { tags: { action: `menu_${variant}`, menuName: data.name } });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Typography size="xxlarge" weight="semi-bold" className={styles['title']}>
        {title}
      </Typography>

      <div className={styles['text-inputs']}>
        <Input
          type="text"
          label="Menu name"
          hasMargin
          register={register('name', { required: 'Please enter a menu name' })}
          error={errors?.name?.message}
        />
        <Input
          type="text"
          label="Consumer name"
          hasMargin
          helperText="Name seen when the menu is published"
          register={register('consumerName')}
        />

        {variant === FormVariant.CREATE && (
          <ProductRangeSelector
            ranges={ranges}
            register={register}
            clearErrors={clearErrors}
            errors={errors}
            setValue={setValue}
            watch={watch}
            productType={'BUNDLE'}
            control={control}
          />
        )}

        <ImageUpload
          title="Image"
          prompt="Upload Image"
          image={uploadedImage}
          shouldDeleteImage={false}
          setImage={setUploadedImage}
          uploadType="image"
        />
      </div>

      <PrimaryButton data-cy="create-button" loading={isSubmitting} size="full-width" type="submit">
        {buttonTextLookup[variant]}
      </PrimaryButton>
      <div className={styles['error-banner']}>
        {errorMessage && <StatusBar message={errorMessage} variant="error" />}
      </div>
    </form>
  );
};

export default MenuModalContent;
